import {
  useWSSnackbar,
  WSCheckboxToggle,
  WSInfoBox,
  WSList,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayeeTaxDocumentSharePermission,
  PayerPayeeStatus
} from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React, { useState } from "react";
import { useQueryPayerRows } from "../../../query/search/payer/queries/useQueryPayerRows";
import { useMutationSubmit } from "./useMutationSubmit";

export type FormW9ConsentProps = {
  onClose: () => void;
  payerId?: string;
};

export const W9Consent: React.FC<FormW9ConsentProps> = ({
  onClose,
  payerId,
  ...elementProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [shareW9, setShareW9] = useState(false);
  const [submit, submitMeta] = useMutationSubmit({
    onSuccess() {
      openSnackbar({
        type: "success",
        duration: 3000,
        message: `"Consent to share W-9 information" complete`
      });
      onClose();
    }
  });

  const queryPayers = useQueryPayerRows({
    filter: {
      payerId: payerId,
      "payeeOwnedData.status": PayerPayeeStatus.Active,
      "payeeOwnedData.shareTaxDocument": {
        in: [
          PayeeTaxDocumentSharePermission.Decline,
          PayeeTaxDocumentSharePermission.Unspecified
        ]
      }
    },
    page: {
      size: 100
    }
  });

  const names = queryPayers.data
    ?.map(row => {
      const names = wsName({
        user: row.user,
        member: row.member
      });

      return names.getResolvedName();
    })
    .join(", ");

  const payerIds = queryPayers.data?.map(row => row.payerId) || [];

  return (
    <WSModal
      size="S"
      title="Share information with client"
      divider
      actions={{
        alignment: "fill",
        buttons: [
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Tertiary"
          },
          {
            label: "Continue",
            onClick: () => submit(payerIds),
            kind: "Primary",
            loading: submitMeta.isLoading,
            disabled: !shareW9
          }
        ]
      }}
      onClose={onClose}
    >
      <WSList gap="XL">
        <WSInfoBox>
          <WSText.ParagraphSm weight="medium">
            A client is requesting access to your information
          </WSText.ParagraphSm>
          <WSText.ParagraphSm>
            Your client, {names}, is requesting access to your taxpayer
            information or documents in order to fulfill eligibility
            requirements.
          </WSText.ParagraphSm>
        </WSInfoBox>

        <WSCheckboxToggle
          shimmer={queryPayers.isLoading}
          name="shareW9"
          label={`I consent to share taxpayer information (including documents such as Form W-9 and ID verification) with my client(s) (${names}). I understand this information will be used for compliance and eligibility purposes in accordance with applicable laws and regulations.`}
          value={shareW9}
          onChange={value => {
            setShareW9(value);
          }}
        />
      </WSList>
    </WSModal>
  );
};
